import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Toaster } from 'sonner';
import '@/App.css';
import { BrandingProvider } from '@/contexts/BrandingContext';
import { LanguageProvider } from '@/i18n/LanguageContext';
import { ThemeProvider } from '@/contexts/ThemeProvider';

// Pages
import LandingPage from '@/pages/LandingPage';
import SuperAdminLogin from '@/pages/SuperAdminLogin';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import CompanyAdminLogin from '@/pages/CompanyAdminLogin';
import CompanyAdminSignup from '@/pages/CompanyAdminSignup';
import VerifyEmail from '@/pages/VerifyEmail';
import CompanyAdminDashboard from '@/pages/CompanyAdminDashboard';
import StaffLogin from '@/pages/StaffLogin';
import StaffDashboard from '@/pages/StaffDashboard';
import AuthCallback from '@/pages/AuthCallback';
import TermsAndConditions from '@/pages/TermsAndConditions';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import PartnerLogin from '@/pages/PartnerLogin';
import PartnerDashboard from '@/pages/PartnerDashboard';
import ProtectedRoute from '@/components/ProtectedRoute';
import AnalyticsRouteTracker from '@/components/AnalyticsRouteTracker';
import ScrollToTop from '@/components/ScrollToTop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - SYNCHRONOUS CHECK
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <AnalyticsRouteTracker />
      <ScrollToTop />
      <Routes>
      {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Super Admin Routes */}
      <Route path="/guru" element={<SuperAdminLogin />} />
      <Route path="/superadmin/*" element={
        <ProtectedRoute requiredRole="superadmin">
          <SuperAdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Company Admin Routes */}
      <Route path="/admin" element={<CompanyAdminLogin />} />
      <Route path="/admin/signup" element={<CompanyAdminSignup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/admin/*" element={
        <ProtectedRoute requiredRole="admin">
          <CompanyAdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Staff Routes */}
      <Route path="/login" element={<StaffLogin />} />
      <Route path="/dashboard/*" element={
        <ProtectedRoute requiredRole="staff">
          <StaffDashboard />
        </ProtectedRoute>
      } />
      
      {/* Partner Routes */}
      <Route path="/partner" element={<PartnerLogin />} />
      <Route path="/partner/*" element={
        <ProtectedRoute requiredRole="partner">
          <PartnerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* Default redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <BrandingProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster richColors position="top-right" closeButton />
          </BrandingProvider>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;